import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  FirstPage,
  LastPage
} from '@mui/icons-material';
import './Pagination.css';

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Generate page numbers to display
  const getPageNumbers = () => {
    const delta = isMobile ? 1 : 2; // Show fewer pages on mobile
    const range = [];
    const rangeWithDots = [];
    
    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }
    
    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }
    
    rangeWithDots.push(...range);
    
    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }
    
    return rangeWithDots;
  };
  
  const handlePageClick = (page) => {
    if (page !== '...' && page !== currentPage) {
      onPageChange(page);
    }
  };
  
  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };
  
  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };
  
  const handleFirst = () => {
    if (currentPage !== 1) {
      onPageChange(1);
    }
  };
  
  const handleLast = () => {
    if (currentPage !== totalPages) {
      onPageChange(totalPages);
    }
  };
  
  if (totalPages <= 1) {
    return null;
  }
  
  const pageNumbers = getPageNumbers();
  
  return (
    <Box className="pagination-container">
      <Box className="pagination-wrapper">
        {/* First Page Button (Desktop only) */}
        {!isMobile && (
          <IconButton
            onClick={handleFirst}
            disabled={currentPage === 1}
            className="pagination-nav-btn"
            aria-label="Première page"
          >
            <FirstPage />
          </IconButton>
        )}
        
        {/* Previous Button */}
        <IconButton
          onClick={handlePrevious}
          disabled={currentPage === 1}
          className="pagination-nav-btn"
          aria-label="Page précédente"
        >
          <ChevronLeft />
        </IconButton>
        
        {/* Page Numbers */}
        <Box className="pagination-numbers">
          {pageNumbers.map((page, index) => {
            if (page === '...') {
              return (
                <Typography
                  key={`dots-${index}`}
                  className="pagination-dots"
                  variant="body2"
                >
                  ...
                </Typography>
              );
            }
            
            return (
              <Button
                key={page}
                onClick={() => handlePageClick(page)}
                className={`pagination-number ${
                  page === currentPage ? 'active' : ''
                }`}
                variant={page === currentPage ? 'contained' : 'text'}
                size="small"
                aria-label={`Page ${page}`}
                aria-current={page === currentPage ? 'page' : undefined}
              >
                {page}
              </Button>
            );
          })}
        </Box>
        
        {/* Next Button */}
        <IconButton
          onClick={handleNext}
          disabled={currentPage === totalPages}
          className="pagination-nav-btn"
          aria-label="Page suivante"
        >
          <ChevronRight />
        </IconButton>
        
        {/* Last Page Button (Desktop only) */}
        {!isMobile && (
          <IconButton
            onClick={handleLast}
            disabled={currentPage === totalPages}
            className="pagination-nav-btn"
            aria-label="Dernière page"
          >
            <LastPage />
          </IconButton>
        )}
      </Box>
      
      {/* Page Info */}
      <Typography variant="body2" className="pagination-info">
        Page {currentPage} sur {totalPages}
      </Typography>
    </Box>
  );
};

export default Pagination;